import AddShoppingCartRoundedIcon from "@mui/icons-material/AddShoppingCartRounded";
import ExpandCircleDownOutlinedIcon from "@mui/icons-material/ExpandCircleDownOutlined";
import {
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  IconButton,
  Accordion as MuiAccordion,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { getFiltersPrices } from "api/filters";
import NumberInput from "components/NumberInput";
import { useCart } from "context/CartContext";
import { useUserContext } from "context/userContext";
import React, { useMemo, useRef, useState, useEffect } from "react";

const MachineDisplay = ({ machine }, handleAddToCart) => {
  const { addToRequestForm } = useCart();
  const { isAuth } = useUserContext();

  const handleAddMachineToRequestForm = (machine, quantity = 1) => {
    machine = {
      id: machine.id,
      name: `${machine.name} (${machine.brand_name})`,
      type: "machine",
    };
    addToRequestForm(machine, quantity);
  };

  return (
    <AccordionSummary
      aria-controls={`panel${machine.id}d-content`}
      id={`panel${machine.id}d-header`}
    >
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
            {machine.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            <span>
              <strong>Motor Type:</strong> {machine.engine_type || "N/A"}
            </span>
            <br />
            <span>
              <strong>Mærke:</strong>{" "}
              {isAuth
                ? machine.engine_type || "N/A"
                : machine.brand_name || "N/A"}
            </span>
            <br />
            <span>
              <strong>Årstal:</strong> {machine.manufacturing_year || "N/A"}
            </span>
          </Typography>
        </Box>
        {isAuth ? (
          <IconButton aria-label={`expand details for ${machine.name}`}>
            <ExpandCircleDownOutlinedIcon />
          </IconButton>
        ) : (
          <Box
            sx={{
              width: "47px",
              height: "40px",
              backgroundColor: "#424242",
              borderRadius: "5px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              "&:hover": {
                backgroundColor: "#686868",
              },
              flexShrink: 0,
            }}
          >
            <IconButton
              aria-label="Add To Cart"
              size="large"
              onClick={() => handleAddMachineToRequestForm(machine, 1)}
            >
              <AddShoppingCartRoundedIcon sx={{ color: "#ffffff" }} />
            </IconButton>
          </Box>
        )}
      </Stack>
    </AccordionSummary>
  );
};

export const Machines = ({ machine }) => {
  const { isAuth } = useUserContext();
  return (
    <>
      {isAuth ? (
        <MachineDisplay machine={machine} />
      ) : (
        <Paper>
          <MachineDisplay machine={machine} />
        </Paper>
      )}
    </>
  );
};

const MachinesFilter = ({ filters }) => {
  const theme = useTheme();
  const { isAuth } = useUserContext();
  const [selectedFilters, setSelectedFilters] = useState({});
  const [quantity, setQuantity] = useState({});
  const { addMultipleToCart } = useCart();
  const isAnySelected = useMemo(
    () => Object.values(selectedFilters).some(Boolean),
    [selectedFilters]
  );

  const handleQuantityChange = (id, value) => {
    setQuantity((prev) => ({
      ...prev,
      [id]: Math.max(1, Math.min(99, parseInt(value) || 0)),
    }));
  };

  useEffect(() => {
    const initialQuantities = {};
    filters.forEach((filter) => {
      initialQuantities[filter.id] = filter.amount || 1;
    });
    setQuantity(initialQuantities);
  }, [filters]);

  const handleCheckboxChange = (id) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleAddToCart = () => {
    const selectedItems = [];
    Object.entries(selectedFilters).forEach(([filterId, isSelected]) => {
      if (isSelected) {
        const filter = filters.find((f) => f.id === parseInt(filterId));
        const numberOfItems = quantity[filter.id] || 1;
        selectedItems.push({
          ...filter,
          quantity: numberOfItems,
          type: "product",
        });
      }
    });
    selectedItems.sort(
      (a, b) => a.subcategory_priority - b.subcategory_priority
    );
    addMultipleToCart(
      selectedItems.map((row) => ({
        filter_id: row.id,
        quantity: row.quantity,
        ref: row.jl_number,
        type: row.type,
      }))
    );
  };
  const handleSelectAll = () => {
    const allSelected = filters.every((filter) => selectedFilters[filter.id]);
    const newSelectedState = !allSelected;
    const newSelectedFilters = {};
    filters.forEach((filter) => {
      newSelectedFilters[filter.id] = newSelectedState;
    });
    setSelectedFilters(newSelectedFilters);
  };

  return (
    <AccordionDetails sx={{ padding: "3px 3px 1px" }}>
      {isAuth &&
        filters.map((filter) => (
          <Card
            key={filter.id}
            sx={{
              display: "flex",
              flexDirection: "row",
              height: "100%",
              width: "100%",
              marginY: "10px",
              bgcolor: theme.palette.grey[50],
              overflow: "hidden",
              boxSizing: "border-box",
            }}
            elevation={3}
          >
            <CardContent
              sx={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                padding: "16px",
                boxSizing: "border-box",
                overflow: "hidden",
              }}
            >
              <Box
                display={"flex"}
                flexDirection={"row"}
                alignItems={"center"}
                gap={"10px"}
              >
                <Box
                  sx={{
                    maxHeight: 50,
                    maxWidth: 50,
                    overflow: "hidden",
                    my: 2,
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={
                      filter.s_icon
                        ? `https://minio.jlfiltersystem.dk/uploads/${filter.s_icon}`
                        : "/images/filter_1836109.png"
                    }
                    alt={filter.s_name}
                    style={
                      filter.s_icon
                        ? { width: "100%", height: "auto" }
                        : { width: "80%", height: "auto" }
                    }
                  />
                </Box>

                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    JL Number: {filter?.amount}/{filter?.jl_number}
                  </Typography>
                  <Typography variant="h8" sx={{ fontWeight: "bold", mb: 1 }}>
                    {filter.referencer}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Type: {filter.s_name}
                  </Typography>
                </Box>
              </Box>

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="flex-end"
              >
                <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                  {filter.loading ? (
                    <Typography variant="body2" color={"#FF9100"}>
                      <strong>Loading...</strong>
                    </Typography>
                  ) : filter.price === null ? (
                    <Typography variant="body2" color={"#FF9100"}>
                      <strong>Ring for pris</strong>
                    </Typography>
                  ) : filter.price?.Unit_Price ===
                    filter.price?.Unit_Price_Discount ? (
                    <Typography variant="body1" color={"#00712D"}>
                      <strong>
                        {" "}
                        {filter.price?.Unit_Price.toFixed(2)} Dkk
                      </strong>
                    </Typography>
                  ) : (
                    <Typography variant="body1" color={"#00712D"}>
                      <strong>
                        {filter.price?.Unit_Price_Discount.toFixed(2)} Dkk{" "}
                      </strong>
                    </Typography>
                  )}
                </Box>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <NumberInput
                    key={filter.id}
                    value={quantity[filter.id] || filter.amount || 1}
                    onChange={(e, newVal) => {
                      const value = Math.max(1, Math.min(99, newVal));
                      handleQuantityChange(filter.id, value);
                    }}
                    min={1}
                    max={99}
                    sx={{ flexShrink: 0 }}
                  />
                  <Checkbox
                    sx={{
                      color: "#424242",
                      "&.Mui-checked": {
                        color: "#686868",
                      },
                    }}
                    key={filter.id}
                    checked={!!selectedFilters[filter.id]}
                    onChange={() => handleCheckboxChange(filter.id)}
                  />
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Box
          sx={{
            width: "70%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "40px",
            backgroundColor: "#424242",
            borderRadius: "5px",
            "&:hover": {
              backgroundColor: "#686868",
            },
            flexShrink: 0,
          }}
          onClick={() => {
            const selectedFiltersWithQuantity = Object.entries(selectedFilters)
              .filter(([id, selected]) => selected)
              .map(([id]) => ({
                filter: filters.find((f) => f.id === parseInt(id)),
                quantity: quantity[id] || 1,
              }));
            handleAddToCart(selectedFiltersWithQuantity);
          }}
        >
          <IconButton
            aria-label="Add To Cart"
            size="large"
            disabled={!isAnySelected}
            sx={{ marginY: "5px" }}
          >
            <AddShoppingCartRoundedIcon sx={{ color: "#ffffff" }} />
          </IconButton>
        </Box>
        <Box
          sx={{
            width: "30%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={filters.every((filter) => selectedFilters[filter.id])}
                onChange={handleSelectAll}
                sx={{
                  margin: "0 0 0 0",
                  color: "#424242",
                  "&.Mui-checked": {
                    color: "#686868",
                  },
                }}
              />
            }
            label="Alle"
          />
        </Box>
      </Stack>
    </AccordionDetails>
  );
};

export const MachineCard = ({
  expanded,
  handleAccordionChange,
  item: machine,
  quantity,
  handleQuantityChange,
  handleAddToCart,
}) => {
  const [filters, setFilters] = useState(
    machine.filters.map((filter) => ({
      ...filter,
      loading: true,
    }))
  );

  const detailsRef = useRef(null);

  async function GetPrices(machine) {
    const skus = machine.filters.map((filter) => filter.jl_number);

    try {
      const response = await getFiltersPrices(skus);
      setFilters(
        machine.filters.map((filter) => ({
          ...filter,
          price: response.find((p) => p.sku === filter.jl_number) || null,
          loading: false,
        }))
      );
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }

  async function handleAccordionExpanded(event, isExpanded) {
    handleAccordionChange(`panel${machine.id}`)(event, isExpanded);
    if (isExpanded) {
      setFilters((prevFilters) =>
        prevFilters.map((filter) => ({ ...filter, loading: true }))
      );
      await GetPrices(machine);

      if (detailsRef.current) {
        setTimeout(() => {
          const offsetTop = detailsRef.current.getBoundingClientRect().top;
          const scrollPosition = window.pageYOffset + offsetTop - 300;
          window.scrollTo({ top: scrollPosition, behavior: "smooth" });
        }, 100);
      }
    }
  }

  return (
    <MuiAccordion
      expanded={expanded === `panel${machine.id}`}
      onChange={(event, isExpanded) =>
        handleAccordionExpanded(event, isExpanded)
      }
    >
      <Machines machine={machine} />
      {expanded === `panel${machine.id}` && (
        <AccordionDetails ref={detailsRef} sx={{ paddingX: "5px" }}>
          <MachinesFilter
            filters={filters}
            quantity={quantity}
            handleQuantityChange={handleQuantityChange}
            handleAddToCart={handleAddToCart}
          />
        </AccordionDetails>
      )}
    </MuiAccordion>
  );
};
